import { SHDivider, SHStack, SHTypography } from "@components/design-systems";
import { useProduct } from "@hooks/useProduct";
import { ProductFeaturesLayout } from "@layouts/products/features";
import { SubProductList } from "@layouts/products/fees-and-rates/sub-product-list";
import { useTheme } from "@mui/material";

import { RootState } from "@redux/store";
import { isEmpty, isNil } from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { FeeAndRateTabSkeleton } from "../../../edit/components/fees-and-rates/components/skeletons/FeeAndRateTabSekeleton";
import { FRViewFeesTable } from "./components/tables";
import { SpecialSectionNames } from "@pages/suppliers/constants";
import { ConfigurationFeeSectionDTO } from "@models/product/entities/subProduct";

export const ProductViewFeesAndRates = () => {
  const { palette } = useTheme();
  const { productId } = useParams<{ productId: string }>();
  const { loadSubProductsFees } = useProduct();
  const {
    subProducts,
    productUI: { productName, isLoadingSubProducts },
  } = useSelector((state: RootState) => state.product);

  const [selectedSubProductIndex, setSelectedSubProductIndex] =
    useState<number>(0);

  useEffect(() => {
    if (!productId) return;
    loadSubProductsFees(productId, "view");
    // eslint-disable-next-line
  }, [productId]);

  if (isLoadingSubProducts) return <FeeAndRateTabSkeleton />;

  const createFeeTableElement = (
    section: ConfigurationFeeSectionDTO,
    isOtherFee = false,
  ) => (
    <FRViewFeesTable
      fee={section}
      key={section.id}
      isOtherFee={isOtherFee}
    />
  );

  const renderStandardSections = () => {
    const standardSections =
      subProducts?.[selectedSubProductIndex]?.standardFeeSections ?? [];

    return standardSections
      .filter(
        (section) =>
          !SpecialSectionNames.SectionNamesToHide.includes(section.name),
      )
      .map((section) => createFeeTableElement(section, false));
  };

  const renderOtherSections = () => {
    const currentSubProduct = subProducts?.[selectedSubProductIndex];
    const otherSections = currentSubProduct?.otherFeeSections ?? [];
    const standardSections = currentSubProduct?.standardFeeSections ?? [];

    const specialStandardSection = standardSections.find((section) =>
      SpecialSectionNames.SectionNamesToHide.includes(section.name),
    );

    return otherSections.flatMap((section) => {
      const elements = [createFeeTableElement(section, true)];

      if (
        section.name === SpecialSectionNames.FamilyFeeAggregation &&
        specialStandardSection
      ) {
        elements.push(
          createFeeTableElement(specialStandardSection, false),
        );
      }

      return elements;
    });
  };

  return isEmpty(subProducts) ? (
    <></>
  ) : (
    <ProductFeaturesLayout
      leftSide={
        <SubProductList
          subProducts={subProducts}
          selectedSubProductIndex={selectedSubProductIndex}
          onChangeSubProduct={setSelectedSubProductIndex}
        />
      }
      rightSide={
        <SHStack spacing={2}>
          {/* Title */}
          <SHStack>
            <SHTypography
              variant="h2"
              sx={{ fontSize: "24px", fontWeight: 600 }}
            >
              {productName} {subProducts?.[selectedSubProductIndex]?.name}
            </SHTypography>
            <SHTypography variant="body4" sx={{ color: palette.text.fourth }}>
              Fee schedule
            </SHTypography>
          </SHStack>
          <SHDivider />
          {/* Standard Fees Table */}
          {renderStandardSections()}

          {/* Other Fees Table */}
          {!isNil(subProducts?.[selectedSubProductIndex]?.otherFeeSections) && (
            <>
              <SHTypography
                variant={"subtitle2"}
                textAlign="left"
                lineHeight={"160%"}
              >
                Other product fees and information
              </SHTypography>
              {renderOtherSections()}
            </>
          )}
        </SHStack>
      }
      isLoading={false}
    />
  );
};
